import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Mail, Loader2, KeyRound, CheckCircle2, ArrowRight } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPw, setAdminPw] = useState("");
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const requestMagic = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/magic/request", {
        email,
        origin_url: window.location.origin,
      });
      setSent(data.magic_link);
      toast.success("Sign-in link sent to your email");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const adminLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/admin/login", { email: adminEmail, password: adminPw });
      setUser(data);
      toast.success("Welcome back");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0c]">
      <Navbar />
      <div className="mx-auto flex max-w-md flex-col justify-center px-4 py-16">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#ff2a5f]/15 text-[#ff2a5f]">
            <KeyRound className="h-6 w-6" />
          </div>
          <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white">Sign in</h1>
          <p className="mt-2 text-sm text-zinc-400">Access your media library or the admin panel.</p>
        </div>

        <Tabs defaultValue="customer" className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-zinc-900">
            <TabsTrigger data-testid="tab-customer" value="customer">Customer</TabsTrigger>
            <TabsTrigger data-testid="tab-admin" value="admin">Admin</TabsTrigger>
          </TabsList>

          <TabsContent value="customer">
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-6">
              {!sent ? (
                <form onSubmit={requestMagic} className="space-y-4">
                  <div>
                    <Label className="text-zinc-300">Email address</Label>
                    <Input
                      data-testid="magic-link-input"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@email.com"
                      className="mt-1.5 border-zinc-700 bg-zinc-950 text-white"
                    />
                    <p className="mt-2 text-xs text-zinc-500">
                      No password needed. We'll email you a secure one-time sign-in link.
                    </p>
                  </div>
                  <Button
                    data-testid="send-magic-link"
                    type="submit"
                    disabled={busy}
                    className="w-full bg-[#ff2a5f] font-semibold text-white hover:bg-[#e01f50]"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="mr-2 h-4 w-4" /> Send sign-in link</>}
                  </Button>
                </form>
              ) : (
                <div className="space-y-4 text-center" data-testid="magic-sent">
                  <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" />
                  <p className="text-white">Check your inbox for the sign-in link.</p>
                  <p className="text-xs text-zinc-500">
                    Didn't get it? Use the direct link below to continue.
                  </p>
                  <a href={sent} data-testid="magic-direct-link">
                    <Button variant="outline" className="w-full border-zinc-700 bg-transparent text-white hover:bg-zinc-800">
                      Continue to my library <ArrowRight className="ml-1.5 h-4 w-4" />
                    </Button>
                  </a>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="admin">
            <form onSubmit={adminLogin} className="space-y-4 rounded-xl border border-zinc-800 bg-zinc-900/60 p-6">
              <div>
                <Label className="text-zinc-300">Email</Label>
                <Input
                  data-testid="admin-email"
                  type="email"
                  required
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  className="mt-1.5 border-zinc-700 bg-zinc-950 text-white"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Password</Label>
                <Input
                  data-testid="admin-password"
                  type="password"
                  required
                  value={adminPw}
                  onChange={(e) => setAdminPw(e.target.value)}
                  className="mt-1.5 border-zinc-700 bg-zinc-950 text-white"
                />
              </div>
              <Button
                data-testid="admin-login-submit"
                type="submit"
                disabled={busy}
                className="w-full bg-white font-semibold text-black hover:bg-zinc-200"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in to admin"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
